import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import React from "react";

export default class extends Controller {
  static targets = ["container"];

  constructor(props) {
    super(props);
    this.draggedPostElement = null;
    this.futureDateTime = null;
    this.futureContainerDiv = null;
    this.startDivContainer = null;
  }

  connect() {
    // Lorsque handleDialogEvents est appelée en tant que gestionnaire d'événements, this ne fait plus référence à l'instance du contrôleur Stimulus.
    // On corrige avec bind(this) pour qu'elles soient toujours appelée sur l'instance du contrôleur
    this.boundHandleDialogEvents = this.handleDialogEvents.bind(this);
    window.addEventListener("dialog:opened", this.boundHandleDialogEvents);
    window.addEventListener("dialog:closed", this.boundHandleDialogEvents);
    this.bindSubmitButton();
  }

  disconnect() {
    window.removeEventListener("dialog:opened", this.boundHandleDialogEvents);
    window.removeEventListener("dialog:closed", this.boundHandleDialogEvents);
    this.unbindSubmitButton();
  }

  handleDialogEvents(event) {
    if (event.detail.closed) {
      // On enlève draggedPostElement de la futureDiv
      this.draggedPostElement.remove();
      // On remet draggedPostElement dans l'ancienne div
      this.startDivContainer.appendChild(this.draggedPostElement);

      // On annule toute possibilité de draggable
      this.draggedPostElement.draggable = false;

      // On rafraichi la page
      Turbo.visit(window.location.href);
    }
  }

  bindSubmitButton() {
    const submitBtn = this.getSubmitButton();
    if (!submitBtn) return;

    this.boundShowSpinner = this.submitAction.bind(this);
    submitBtn.addEventListener("click", this.boundShowSpinner);
  }

  unbindSubmitButton() {
    const submitBtn = this.getSubmitButton();
    if (!submitBtn) return;

    submitBtn.removeEventListener("click", this.boundShowSpinner);
  }

  submitAction(event) {
    const submitBtn = this.getSubmitButton();
    submitBtn.querySelector("span")?.remove();
    const spinner = event.currentTarget.querySelector("svg");
    if (spinner) spinner.classList.remove("hidden");
  }

  getSubmitButton() {
    return document.querySelector(
      "#form_post_scheduled_at button[type='submit']"
    );
  }

  dragStart(event) {
    if (!event.currentTarget.draggable) { return };
    this.draggedPostElement = event.currentTarget;
    this.startDivContainer = event.currentTarget.parentElement;
  }

  dragOver(event) {
    event.preventDefault();
    const element = event.currentTarget;

    // On formate la date future
    const futureDateElement = element.querySelector("a");
    const futureDateTime = futureDateElement?.dataset?.datetime;

    // On instancie la future date
    if (futureDateTime) {
      this.futureDateTime = new Date(futureDateTime);
    } else {
      console.error("La date n'est pas accessible");
    }

    // On retire toutes les card-over
    this.removeCardExcept(element);
    this.futureContainerDiv = element;

    // On arrête là si le post est déjà dans le 'futureContainerDiv'
    const currentPostIsAlreadyInFutureDiv = Array.from(
      element.querySelectorAll("a")
    ).includes(this.draggedPostElement);
    if (currentPostIsAlreadyInFutureDiv) {
      this.removeAllOverCards(document);
      return;
    }

    // On ajoute une carte over seulement si y'en a pas
    const elementHasOverCard = element.querySelector(".card-over");
    if (!elementHasOverCard) {
      this.addDragOverCard(element);
    }
  }

  dragLeave(event) {
    event.preventDefault();
    const element = event.currentTarget;

    const currentPostIsAlreadyInFutureDiv = Array.from(
      this.futureContainerDiv.querySelectorAll("a")
    ).includes(this.draggedPostElement);
    if (currentPostIsAlreadyInFutureDiv) {
      return;
    }

    if (element.nodeName === "A" && this.futureDateTime) {
      // On prend la route du lien pour la future requête UPDATE
      const updatePath = event.currentTarget.href;
      if (updatePath) {
        const postOnChannels = JSON.parse(event.currentTarget.dataset.pocs);
        // On reconstruit le body de la requête
        const body = {
          post: {
            post_on_channels_attributes: postOnChannels.map((poc) => ({
              id: poc.id,
              forced_scheduled_at: this.futureDateTime,
              status: poc.status,
            })),
            scheduled_at: this.futureDateTime,
          },
        };

        this.updatePost(updatePath, body);

        // On déplace le DOM
        this.addCard(element);
      }
    }
  }

  updatePost(route, data = {}) {
    // On enlève toutes les cartes over
    this.removeAllOverCards(document);

    // On regarde si tous les pocs ont un status "scheduled" sinon on coupe tout
    const allPocsAreScheduled = data?.post?.post_on_channels_attributes?.every(
      (poc) => poc.status === "scheduled"
    );

    if (allPocsAreScheduled) {
      // On déclenche l'event pour que la bonne modal s'ouvre
      window.dispatchEvent(
        new CustomEvent("open-dialog", {
          detail: {
            id: "edit_post_scheduled_at",
          },
        })
      );
    }
    // On construit le formulaire de la modal
    const dialogBody = document.querySelector("#form-post-scheduled-at-update");

    // On enlève tous les précédents enfants du formulaire
    Array.from(dialogBody.children).forEach((child) => child.remove());

    // On construit le form
    this.buildFormEditPostHTML(
      dialogBody,
      route,
      this.futureDateTime,
      data.post.post_on_channels_attributes,
      data.post.scheduled_at
    );
  }

  removeCardExcept(element) {
    Array.from(this.containerTargets).forEach((container) => {
      if (container !== element) {
        container.classList.remove("relative");
        this.removeAllOverCards(container);
      }
    });
  }

  removeAllOverCards(divContainer) {
    const overCards = divContainer.querySelectorAll(".card-over");
    Array.from(overCards).forEach((card) => {
      card.remove();
    });
  }

  addDragOverCard(element) {
    element.classList.add("relative");
    const overCard = document.createElement("div");
    overCard.classList.add(
      "w-full",
      "h-[48px]",
      "border-2",
      "border-dashed",
      "shadow-sm",
      "card-over"
    );
    element.appendChild(overCard);
  }

  addCard(card) {
    // On supprime tous les enfants de la div parente
    Array.from(this.futureContainerDiv.children).forEach((child) => {
      if (child.querySelector("a")?.dataset?.visible || child?.dataset?.visible)
        return;
      child.remove();
    });

    // On reconstruit la div avec la carte du post déplacée
    const fullCel = document.createElement("div");
    fullCel.classList.add("p-1", "space-y-1", "h-fit", "flex", "flex-col");
    fullCel.appendChild(card);

    // On affiche la carte du post dans la future div
    this.futureContainerDiv.appendChild(fullCel);
  }

  buildFormEditPostHTML(div, route, datetime, pocs, scheduled_at) {
    const days = [
      "dimanche",
      "lundi",
      "mardi",
      "mercredi",
      "jeudi",
      "vendredi",
      "samedi",
    ];
    const date = new Date(datetime);

    const form = document.getElementById("form_post_scheduled_at");
    form.action = route;

    // Message de confirmation
    const messageEl = form.querySelector("span");
    messageEl.innerText = `En validant, le post sera programmé le ${
      days[date.getDay()]
    } ${date.toLocaleDateString("fr-FR")} à ${date.toLocaleTimeString(
      "fr-FR"
    )}`;

    // Input : Post.scheduled_at
    const input = document.createElement("input");
    input.name = "post[scheduled_at]";
    input.value = scheduled_at;
    input.classList.add("hidden");
    div.appendChild(input);

    // Inputs : POCS
    pocs.forEach((poc, index) => {
      const inputDate = document.createElement("input");
      inputDate.name = `post[post_on_channels_attributes][${index}][forced_scheduled_at]`;
      inputDate.value = poc.forced_scheduled_at;
      inputDate.classList.add("hidden");
      const inputId = document.createElement("input");
      inputId.name = `post[post_on_channels_attributes][${index}][id]`;
      inputId.value = poc.id;
      inputId.classList.add("hidden");
      div.appendChild(inputId);
      div.appendChild(inputDate);
    });
  }
}
